import { Alert, Button, Card, Input, Space, Typography, Upload } from "antd";
import type { UploadProps } from "antd";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Dialog as ZhDialog,
    Grid as ZhGrid
} from "zhihao-ui-react";

import request from "@/api";
import type {
    CancelReceivePaymentForm,
    ConfirmReceivePaymentForm,
    File as EntityFile,
    ReceivePayment,
    ReceivePaymentVo
} from "@zhihaoscm/entity";
import { RECEIVE_PAYMENT_TYPE, PAYMENT_SUBMIT_TYPE } from "@zhihaoscm/meta";

type TradeCollectionVoucherFile = Pick<EntityFile, "id" | "name" | "path">;

export interface TradeCollectionDetailPageProps {
    loadDetail?: (id: number | string) => Promise<ReceivePaymentVo>;
    assignPayment?: (payload: { orderId: number; handlerId: string }) => Promise<unknown>;
    updatePayment?: (id: number | string, payload: Partial<ReceivePayment>) => Promise<unknown>;
    confirmPayment?: (payload: ConfirmReceivePaymentForm) => Promise<unknown>;
    uploadPaymentFile?: (file: Blob) => Promise<TradeCollectionVoucherFile>;
    previewVoucher?: (id: number | string, fileIds: number[]) => Promise<string | undefined>;
    cancelPayment?: (payload: CancelReceivePaymentForm) => Promise<unknown>;
    revokePayment?: (payload: CancelReceivePaymentForm) => Promise<unknown>;
    deletePayment?: (id: number | string) => Promise<unknown>;
    permissions?: string[];
    currentUserId?: number;
}

const PAYMENT_TYPE_LABELS: Record<number, string> = {
    1: RECEIVE_PAYMENT_TYPE.ORDER_DEPOSIT.intro,
    2: RECEIVE_PAYMENT_TYPE.ADVANCE_PAYMENT.intro,
    3: RECEIVE_PAYMENT_TYPE.SHIPPING_PAYMENT.intro,
    4: RECEIVE_PAYMENT_TYPE.WAREHOUSE_OPENING_PAYMENT.intro,
    5: RECEIVE_PAYMENT_TYPE.ORDER_PAYMENT.intro
};

const RECEIVE_STATE_LABELS: Record<number, string> = {
    1: "待支付",
    2: "已支付 (线上)",
    3: "未支付",
    4: "待确认支付",
    5: "已支付 (线下)"
};

/** 将金额统一为 Vue 详情页使用的两位小数格式。 */
function formatMoney(value: number | undefined): string {
    return (Number(value) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** 沿用 Vue BaseService.get 的收款详情接口路径。 */
export async function loadTradeCollectionDetail(id: number | string): Promise<ReceivePaymentVo> {
    const response = await request.service.get<ReceivePaymentVo>(`/admin/receive-payment/${id}`);
    return response.data;
}

/** 沿用 Vue 订单专员指派接口。 */
export async function assignTradeCollection(payload: { orderId: number; handlerId: string }): Promise<unknown> {
    const response = await request.service.put(`/admin/order/assign/${payload.orderId}/${payload.handlerId}`);
    return response.data;
}

/** 沿用 Vue 收款编辑接口，保留现有收款表单 payload。 */
export async function updateTradeCollection(id: number | string, payload: Partial<ReceivePayment>): Promise<unknown> {
    const response = await request.service.put(`/admin/receive-payment/${id}`, payload);
    return response.data;
}

/** 沿用 Vue 确认收款接口。 */
export async function confirmTradeCollection(payload: ConfirmReceivePaymentForm): Promise<unknown> {
    const response = await request.service.put("/admin/receive-payment/confirm", payload);
    return response.data;
}

/** 复用 Vue file-list 的上传接口，将付款凭证文件交给文件服务保存。 */
export async function uploadTradeCollectionVoucher(file: Blob): Promise<TradeCollectionVoucherFile> {
    const formData = new FormData();
    formData.append("file", file);
    // 延迟加载既有文件服务，避免详情页首屏引入服务装饰器链，同时保持 fileService.uploadFile 契约。
    const { fileService } = await import("@/api/services/fileService");
    return fileService.uploadFile(formData);
}

/** 查询 Vue 收款凭证文件服务并返回地址。 */
export async function previewTradeCollectionVoucher(
    _id: number | string,
    fileIds: number[]
): Promise<string | undefined> {
    const fileId = fileIds[0];
    if (!fileId) return undefined;
    const response = await request.service.get<{ path?: string }>(`/dataService/admin/file/${fileId}`);
    return response.data.path;
}

/** 沿用 Vue 取消收款接口。 */
export async function cancelTradeCollection(payload: CancelReceivePaymentForm): Promise<unknown> {
    const response = await request.service.put("/admin/receive-payment/cancel", payload);
    return response.data;
}

/** 沿用 Vue 撤回收款接口。 */
export async function revokeTradeCollection(payload: CancelReceivePaymentForm): Promise<unknown> {
    const response = await request.service.put("/admin/receive-payment/revoke", payload);
    return response.data;
}

/** 沿用 Vue 删除收款接口。 */
export async function deleteTradeCollection(id: number | string): Promise<unknown> {
    const response = await request.service.delete(`/admin/receive-payment/${id}`);
    return response.data;
}

/** 将采购企业信息映射为 Vue 收款详情中的采购账号文案。 */
function getCustomerLabel(detail: ReceivePaymentVo): string {
    const customer = detail.receivePayment.customerEnterprise;
    return `${customer?.name || customer?.realName || "-"} (${customer?.mobile || "-"})`;
}

/** 按 Vue 收款详情的商品类型保留商品详情跳转入口。 */
function getProductDetailPath(payment: ReceivePayment): string | undefined {
    const productId = payment.productId;
    if (!productId) return undefined;
    const paths: Record<number, string> = {
        1: "/productManagement/consignment/detail",
        2: "/productManagement/groupPurchase/detail",
        3: "/productManagement/bargain/detail"
    };
    const path = paths[Number(payment.productType)];
    return path ? `${path}?id=${productId}` : undefined;
}

/** 交易管理收款详情首屏，按 Vue 页面保留关联信息、账户信息和费用信息分组。 */
export function TradeCollectionDetailPage({
    loadDetail = loadTradeCollectionDetail,
    assignPayment = assignTradeCollection,
    updatePayment = updateTradeCollection,
    confirmPayment = confirmTradeCollection,
    uploadPaymentFile = uploadTradeCollectionVoucher,
    previewVoucher = previewTradeCollectionVoucher,
    cancelPayment = cancelTradeCollection,
    revokePayment = revokeTradeCollection,
    deletePayment = deleteTradeCollection,
    permissions = [],
    currentUserId
}: TradeCollectionDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const collectionId = new URLSearchParams(location.search).get("id") || "";
    const [detail, setDetail] = useState<ReceivePaymentVo | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [assignVisible, setAssignVisible] = useState(false);
    const [handlerIdInput, setHandlerIdInput] = useState("");
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [confirmFileIds, setConfirmFileIds] = useState<number[]>([]);
    const [confirmFileNames, setConfirmFileNames] = useState<string[]>([]);
    const [confirmUploading, setConfirmUploading] = useState(false);
    const [confirmUploadError, setConfirmUploadError] = useState("");

    const hasPermission = (permission: string) => permissions.includes(permission);

    useEffect(() => {
        if (!collectionId) {
            setErrorMessage("缺少收款编号");
            return;
        }
        let disposed = false;
        void loadDetail(collectionId)
            .then(result => {
                if (!disposed) {
                    setDetail({
                        ...result,
                        receivePayment: { ...result.receivePayment, state: Number(result.receivePayment.state) }
                    });
                    setErrorMessage(null);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("收款详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [collectionId, loadDetail]);

    const payment = detail?.receivePayment;
    const showPaymentMethod = payment && ![1, 3].includes(Number(payment.state));
    const showPaymentInstruction = payment && [4, 5].includes(Number(payment.state));
    const latestConfirm = payment?.confirmOperateInfo?.[payment.confirmOperateInfo.length - 1];

    /** Vue 的当前专员判断用于限制修改和确认付款。 */
    const isHandler = Boolean(detail?.handlerId && currentUserId && detail.handlerId === currentUserId);
    const canManage = hasPermission("ROLE_RECEIVABLES_MANAGE");
    const canDeal = hasPermission("ROLE_RECEIVABLES_DEAL");
    const showEdit = payment && Number(payment.state) === 1 && isHandler && hasPermission("ROLE_TRADE_ORDER_DEAL");
    const showConfirm = payment && isHandler && (canDeal || canManage) && [1, 4].includes(Number(payment.state));
    /** 沿用 Vue 详情页权限契约，仅凭证查看角色可访问凭证入口。 */
    const showVoucher =
        payment &&
        hasPermission("ROLE_RECEIVABLES_R") &&
        ((payment.customFileId?.length && [3, 4].includes(Number(payment.state))) ||
            (Number(payment.state) === 5 && payment.paymentFileId?.length));
    const showCancel =
        payment &&
        canManage &&
        ((![1, 5].includes(Number(payment.paymentType)) && Number(payment.state) === 1) ||
            [4, 5].includes(Number(payment.state)));
    const showDelete =
        detail &&
        canManage &&
        Boolean(detail.orderDel || detail.orderState === 6) &&
        payment &&
        [3, 5].includes(Number(payment.state));

    /** 提交订单专员指派，保留 Vue 的 orderId/handlerId 路径契约。 */
    const submitAssign = async () => {
        if (!payment || !handlerIdInput.trim()) return;
        try {
            await assignPayment({ orderId: Number(payment.orderId), handlerId: handlerIdInput.trim() });
            setAssignVisible(false);
        } catch {
            setErrorMessage("收款指派失败");
        }
    };

    /** 以当前收款字段提交 Vue 编辑接口，页面入口保持可用。 */
    const editPayment = async () => {
        if (!payment) return;
        try {
            await updatePayment(payment.id, {
                remark: payment.remark,
                paymentInstruction: payment.paymentInstruction
            });
        } catch {
            setErrorMessage("收款修改失败");
        }
    };

    /** 打开确认付款弹窗时同步已有凭证，保持 Vue file-list 的编辑回显行为。 */
    const openConfirm = () => {
        setConfirmFileIds(payment?.paymentFileId || []);
        setConfirmFileNames([]);
        setConfirmUploadError("");
        setConfirmVisible(true);
    };

    /** 对齐 Vue file-list：限制凭证数量、大小和类型，上传成功后回填文件 ID。 */
    const beforeConfirmUpload: UploadProps["beforeUpload"] = file => {
        const fileName = file.name.toLowerCase();
        const acceptedExtensions = [
            ".zip",
            ".rar",
            ".7z",
            ".aip",
            ".jpg",
            ".jpeg",
            ".png",
            ".webp",
            ".avif",
            ".doc",
            ".docx",
            ".xls",
            ".xlsx",
            ".pdf"
        ];
        if (confirmFileIds.length >= 10) {
            setConfirmUploadError("付款凭证最多上传10个文件");
            return Upload.LIST_IGNORE;
        }
        if (file.size > 50 * 1024 * 1024) {
            setConfirmUploadError("付款凭证单个文件不能超过50M");
            return Upload.LIST_IGNORE;
        }
        if (!acceptedExtensions.some(extension => fileName.endsWith(extension))) {
            setConfirmUploadError("上传失败，仅支持图片、文档和压缩包文件");
            return Upload.LIST_IGNORE;
        }

        setConfirmUploadError("");
        setConfirmUploading(true);
        void uploadPaymentFile(file)
            .then(uploadedFile => {
                if (!uploadedFile?.id) throw new Error("上传响应缺少文件 ID");
                // 文件服务返回的 ID 是确认付款接口唯一需要的附件字段。
                setConfirmFileIds(current => [...current, Number(uploadedFile.id)]);
                setConfirmFileNames(current => [...current, uploadedFile.name || file.name]);
            })
            .catch(() => {
                setConfirmUploadError("付款凭证上传失败");
            })
            .finally(() => {
                setConfirmUploading(false);
            });
        return false;
    };

    /** 提交确认付款，保留 Vue imgIds 必填校验及 fileIds 请求契约。 */
    const submitConfirm = async () => {
        if (!payment) return;
        if (!confirmFileIds.length) {
            setConfirmUploadError("请上传付款凭证");
            return;
        }
        try {
            await confirmPayment({ id: payment.id, fileIds: confirmFileIds });
            setConfirmVisible(false);
        } catch {
            setErrorMessage("确认付款失败");
        }
    };

    /** 查看收款凭证。 */
    const viewVoucher = async () => {
        if (!payment) return;
        try {
            const path = await previewVoucher(payment.id, payment.paymentFileId || payment.customFileId || []);
            if (path) window.open(path, "_blank");
        } catch {
            setErrorMessage("收款凭证加载失败");
        }
    };

    /** 按 Vue 状态选择取消或撤回接口。 */
    const cancelOrRevoke = async () => {
        if (!payment) return;
        const payload = { id: payment.id, reason: "" };
        try {
            if (Number(payment.state) === 5) await revokePayment(payload);
            else await cancelPayment(payload);
        } catch {
            setErrorMessage("收款取消失败");
        }
    };

    /** 删除已取消订单关联的收款记录。 */
    const removePayment = async () => {
        if (!payment) return;
        try {
            await deletePayment(payment.id);
        } catch {
            setErrorMessage("收款删除失败");
        }
    };

    return (
        <section aria-label="trade-collection-detail-page" className="flex h-full min-h-full flex-col">
            <Card>
                {payment ? (
                    <div className="section-card">
                        <ZhDetailHeader
                            leftInfos={[{ label: "收款编号", value: payment.id || "-" }]}
                            rightInfos={[{ label: "创建时间", value: payment.createdTime || "-" }]}
                            stateDesc={RECEIVE_STATE_LABELS[payment.state] || "-"}
                            title="收款详情"
                        />
                    </div>
                ) : null}
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                {payment ? (
                    <>
                        <div className="section-title mb-4 mt-6">基本信息</div>
                        <ZhGrid cols={4} gap={[30, 20]}>
                            <ZhBaseItem label="订单专员" value={detail?.handlerName || "-"} />
                        </ZhGrid>
                        <div className="section-title mb-4 mt-6">关联信息</div>
                        <ZhGrid cols={4} gap={[30, 20]}>
                            <ZhBaseItem
                                label="关联订单"
                                value={
                                    payment.orderId ? (
                                        <Link to={`/tradeManage/tradeOrder/detail?id=${payment.orderId}`}>
                                            {payment.orderId}
                                        </Link>
                                    ) : (
                                        "-"
                                    )
                                }
                            />
                            <ZhBaseItem
                                label="关联商品"
                                value={
                                    getProductDetailPath(payment) ? (
                                        <Link to={getProductDetailPath(payment) as string}>{payment.productId}</Link>
                                    ) : (
                                        payment.productId || "-"
                                    )
                                }
                            />
                            <ZhBaseItem
                                label="关联提货"
                                value={
                                    payment.deliveryId ? (
                                        <Link to={`/purchasingManage/delivery/detail/${payment.deliveryId}`}>
                                            {payment.deliveryId}
                                        </Link>
                                    ) : (
                                        "-"
                                    )
                                }
                            />
                            <ZhBaseItem
                                label="采购账号"
                                value={
                                    payment.customerId ? (
                                        <Link to={`/lianYunUser/userInfo?userId=${payment.customerId}`}>
                                            {getCustomerLabel(detail as ReceivePaymentVo)}
                                        </Link>
                                    ) : (
                                        getCustomerLabel(detail as ReceivePaymentVo)
                                    )
                                }
                            />
                        </ZhGrid>
                        <div className="section-title mb-4 mt-6">付款账户</div>
                        <ZhGrid cols={4} gap={[30, 20]}>
                            <ZhBaseItem label="开户名称" value={payment.payBankInfo?.name || "-"} />
                            <ZhBaseItem label="银行账号" value={payment.payBankInfo?.account || "-"} />
                            <ZhBaseItem label="开户行" value={payment.payBankInfo?.bank || "-"} />
                        </ZhGrid>
                        <div className="section-title mb-4 mt-6">费用信息</div>
                        <ZhGrid cols={4} gap={[30, 20]}>
                            <ZhBaseItem label="费用类型" value={PAYMENT_TYPE_LABELS[payment.paymentType] || "-"} />
                            <ZhBaseItem label="支付金额" value={formatMoney(payment.amount)} />
                            {showPaymentMethod ? (
                                <ZhBaseItem label="付款方式" value={payment.state === 2 ? "线上支付" : "线下支付"} />
                            ) : null}
                            {payment.state === 2 ? (
                                <ZhBaseItem label="支付时间" value={String(payment.payTime || "-")} />
                            ) : null}
                            {payment.state === 5 && latestConfirm ? (
                                <>
                                    <ZhBaseItem label="确认人" value={latestConfirm.confirmUser || "-"} />
                                    <ZhBaseItem label="确认支付凭证时间" value={latestConfirm.confirmTime || "-"} />
                                </>
                            ) : null}
                            {showVoucher ? (
                                <>
                                    <ZhBaseItem
                                        label="凭证提交类型"
                                        value={
                                            Number(payment.submitType) === PAYMENT_SUBMIT_TYPE.CUSTOMER.code
                                                ? "客户提交"
                                                : "平台提交"
                                        }
                                    />
                                    <ZhBaseItem
                                        label="支付凭证"
                                        value={(payment.paymentFileId || payment.customFileId || []).join(", ") || "-"}
                                    />
                                </>
                            ) : null}
                            {showPaymentInstruction ? (
                                <ZhBaseItem label="付款说明" value={payment.paymentInstruction || "-"} />
                            ) : null}
                        </ZhGrid>
                    </>
                ) : null}
                {payment ? (
                    <Space className="mt-4" wrap>
                        {hasPermission("ROLE_TRADE_ORDER_MANAGE") ? (
                            <Button
                                aria-label={detail?.handlerId ? "变更指派" : "指派"}
                                onClick={() => setAssignVisible(true)}
                            >
                                {detail?.handlerId ? "变更指派" : "指派"}
                            </Button>
                        ) : null}
                        {showEdit ? (
                            <Button aria-label="修改" onClick={() => void editPayment()}>
                                修改
                            </Button>
                        ) : null}
                        {showConfirm ? (
                            <Button aria-label="确认付款" onClick={openConfirm}>
                                确认付款
                            </Button>
                        ) : null}
                        {showVoucher ? (
                            <Button aria-label="查看凭证" onClick={() => void viewVoucher()}>
                                查看凭证
                            </Button>
                        ) : null}
                        {showCancel ? (
                            <Button
                                aria-label={Number(payment.state) === 5 ? "撤回" : "取消"}
                                onClick={() => void cancelOrRevoke()}
                            >
                                {Number(payment.state) === 5 ? "撤回" : "取消"}
                            </Button>
                        ) : null}
                        {showDelete ? (
                            <Button aria-label="删除" onClick={() => void removePayment()}>
                                删除
                            </Button>
                        ) : null}
                    </Space>
                ) : null}
                <Button className="mt-4" onClick={() => navigate(-1)}>
                    返回
                </Button>
                <ZhDialog
                    open={assignVisible}
                    title="指派专员"
                    width="842px"
                    onCancel={() => setAssignVisible(false)}
                    footer={
                        <Space>
                            <Button onClick={() => setAssignVisible(false)}>取消</Button>
                            <Button aria-label="提交指派" onClick={() => void submitAssign()} type="primary">
                                提交指派
                            </Button>
                        </Space>
                    }
                >
                    <label className="block">
                        订单专员ID
                        <Input
                            aria-label="订单专员ID"
                            className="mt-2"
                            value={handlerIdInput}
                            onChange={event => setHandlerIdInput(event.target.value)}
                        />
                    </label>
                </ZhDialog>
                <ZhDialog
                    open={confirmVisible}
                    title="是否要帮买主确认支付信息"
                    width={784}
                    onCancel={() => {
                        setConfirmVisible(false);
                        setConfirmUploadError("");
                    }}
                    footer={
                        <Space>
                            <Button onClick={() => setConfirmVisible(false)}>取消</Button>
                            <Button
                                aria-label="提交确认"
                                loading={confirmUploading}
                                onClick={() => void submitConfirm()}
                                type="primary"
                            >
                                提交确认
                            </Button>
                        </Space>
                    }
                >
                    <Typography.Text>确认当前收款单已完成付款？</Typography.Text>
                    <div className="mt-4">
                        <Typography.Text strong>上传支付凭证</Typography.Text>
                        <Upload
                            accept=".zip,.rar,.7z,.aip,.jpg,.jpeg,.png,.webp,.avif,.doc,.docx,.xls,.xlsx,.pdf"
                            beforeUpload={beforeConfirmUpload}
                            showUploadList={false}
                        >
                            <Button className="mt-2" loading={confirmUploading} type="dashed">
                                上传付款凭证
                            </Button>
                        </Upload>
                        <Typography.Text className="ml-2" type="secondary">
                            最多10个文件，单个不超过50M
                        </Typography.Text>
                        {confirmFileNames.length ? (
                            <div className="mt-2">
                                {confirmFileNames.map((name, index) => (
                                    <Typography.Text className="mr-2" key={`${name}-${index}`}>
                                        {name}
                                    </Typography.Text>
                                ))}
                            </div>
                        ) : null}
                        {confirmUploadError ? (
                            <Alert className="mt-2" message={confirmUploadError} role="alert" type="error" showIcon />
                        ) : null}
                    </div>
                </ZhDialog>
            </Card>
        </section>
    );
}

export default TradeCollectionDetailPage;
